// src/components/admin/page-sections/TestimonialsSection.js
import React, { useState } from "react";

const TestimonialsSection = ({ content }) => {
  const {
    title = "What Our Clients Say",
    subtitle = "Hear from businesses who have transformed their packaging with us",
    testimonials = [
      {
        id: 1,
        name: "Sarah Johnson",
        company: "Bright Boutique",
        role: "Owner",
        content:
          "The custom shopping bags exceeded our expectations. Our customers love taking them home and they've become a walking advertisement for our boutique!",
        rating: 5,
        imageUrl: "/images/testimonial1.jpg",
      },
      {
        id: 2,
        name: "Michael Chen",
        company: "Organic Delights",
        role: "Marketing Director",
        content:
          "We needed eco-friendly packaging that aligned with our brand values. Bag & Box delivered beautiful kraft paper bags that our customers appreciate for their sustainability.",
        rating: 5,
        imageUrl: "/images/testimonial2.jpg",
      },
      {
        id: 3,
        name: "Robert Fernandez",
        company: "Luxury Gifts",
        role: "CEO",
        content:
          "The premium gift boxes we ordered have elevated our entire unboxing experience. The attention to detail and quality of materials is exactly what our luxury brand needed.",
        rating: 5,
        imageUrl: "/images/testimonial3.jpg",
      },
    ],
    backgroundColor = "#ffffff",
    titleColor = "#111827",
    subtitleColor = "#4b5563",
    cardBackgroundColor = "#f9fafb",
    cardBorderColor = "#e5e7eb",
    textColor = "#1f2937",
    companyColor = "#6b7280",
    starColor = "#f59e0b",
    buttonColor = "#6b7280",
    buttonHoverColor = "#4b5563",
    showImages = true,
    showRating = true,
    showQuoteIcon = true,
    quoteIconColor = "rgba(0, 0, 0, 0.1)",
    layout = "grid",
    cardsPerRow = {
      mobile: 1,
      tablet: 2,
      desktop: 3,
    },
    paddingTop = "4rem",
    paddingBottom = "4rem",
    marginBottom = "3rem",
    cardBorderRadius = "0.75rem",
    cardShadow = true,
    showDivider = true,
    dividerColor = "#e5e7eb",
    autoplay = true,
    autoplaySpeed = 5000,
    imageShape = "circle",
    showSliderControls = true,
    showSliderDots = true,
    sliderDotsColor = "#d1d5db",
    sliderDotsActiveColor = "#3b82f6",
  } = content || {};

  const [activeSlide, setActiveSlide] = useState(0);

  // Generate star rating
  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, index) => (
      <svg
        key={index}
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4"
        viewBox="0 0 20 20"
        fill={index < rating ? starColor : "none"}
        stroke={starColor}
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  // Determine column classes based on cardsPerRow setting
  const getColumnClasses = () => {
    const { mobile, tablet, desktop } = cardsPerRow;
    let classes = "grid gap-6 ";

    // Mobile first
    classes += `grid-cols-${mobile || 1} `;
    // Tablet breakpoint (md)
    if (tablet) classes += `md:grid-cols-${tablet} `;
    // Desktop breakpoint (lg)
    if (desktop) classes += `lg:grid-cols-${desktop}`;

    return classes;
  };

  // Render a single testimonial card
  const renderTestimonialCard = (testimonial, index) => {
    return (
      <div
        key={testimonial.id || index}
        className="relative transition-all duration-700 ease-out"
        style={{
          backgroundColor: cardBackgroundColor,
          borderRadius: cardBorderRadius,
          border: `1px solid ${cardBorderColor}`,
          ...(cardShadow
            ? {
                boxShadow:
                  "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              }
            : {}),
        }}
      >
        {showQuoteIcon && (
          <div
            className="absolute top-4 right-4"
            style={{ color: quoteIconColor }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M3.691 6.292C5.094 4.771 7.217 4 10.059 4h.381v3.826h-.381c-1.5 0-2.52.398-3.063 1.193-.542.795-.813 1.991-.813 3.588v1.558H10v5.826H3.691V13.167c0-2.75.599-5.152 1.797-7.204M14.309 6.292C15.713 4.771 17.836 4 20.679 4h.321v3.826h-.321c-1.5 0-2.52.398-3.063 1.193-.542.795-.813 1.991-.813 3.588v1.558H20v5.826h-6.309V13.167c0-2.75.599-5.152 1.797-7.204"></path>
            </svg>
          </div>
        )}

        <div className="p-6">
          {showRating && (
            <div className="flex mb-4">{renderStars(testimonial.rating)}</div>
          )}

          <p className="mb-4" style={{ color: textColor }}>
            {testimonial.content}
          </p>

          <div className="flex items-center mt-6">
            {showImages && testimonial.imageUrl && (
              <div
                className={`mr-4 ${
                  imageShape === "circle" ? "rounded-full" : "rounded"
                } overflow-hidden`}
                style={{ width: "48px", height: "48px" }}
              >
                <img
                  src={testimonial.imageUrl}
                  alt={testimonial.name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div>
              <h4 className="font-medium" style={{ color: textColor }}>
                {testimonial.name}
              </h4>

              <p style={{ color: companyColor }}>
                {testimonial.role && `${testimonial.role}, `}
                {testimonial.company}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Navigate to previous slide
  const prevSlide = () => {
    setActiveSlide(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  };

  // Navigate to next slide
  const nextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % testimonials.length);
  };

  // Navigate to specific slide
  const goToSlide = (index) => {
    setActiveSlide(index);
  };

  return (
    <div
      style={{
        backgroundColor,
        paddingTop,
        paddingBottom,
        marginBottom,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        {(title || subtitle) && (
          <div className={`text-center ${showDivider ? "mb-12" : "mb-8"}`}>
            {title && (
              <h2
                className="text-3xl font-bold tracking-tight sm:text-4xl"
                style={{ color: titleColor }}
              >
                {title}
              </h2>
            )}

            {subtitle && (
              <p
                className="mt-3 max-w-2xl mx-auto text-xl"
                style={{ color: subtitleColor }}
              >
                {subtitle}
              </p>
            )}

            {showDivider && (
              <div
                className="mt-6 mx-auto w-24 h-1 rounded"
                style={{ backgroundColor: dividerColor }}
              ></div>
            )}
          </div>
        )}

        {/* Testimonials grid layout */}
        {layout === "grid" && (
          <div className={getColumnClasses()}>
            {testimonials &&
              testimonials.map((testimonial, index) =>
                renderTestimonialCard(testimonial, index)
              )}
          </div>
        )}

        {/* Testimonials slider layout */}
        {layout === "slider" && testimonials && testimonials.length > 0 && (
          <div className="relative">
            <div className="testimonial-slider">
              <div className="relative overflow-hidden">
                <div
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${activeSlide * 100}%)` }}
                >
                  {testimonials.map((testimonial, index) => (
                    <div
                      key={testimonial.id || index}
                      className="w-full flex-shrink-0 px-4"
                    >
                      {renderTestimonialCard(testimonial, index)}
                    </div>
                  ))}
                </div>
              </div>

              {/* Slider controls */}
              {showSliderControls && testimonials.length > 1 && (
                <div className="flex justify-between items-center mt-6">
                  <button
                    type="button"
                    onClick={prevSlide}
                    className="p-2 rounded-full transition-colors duration-200"
                    style={{
                      color: buttonColor,
                      backgroundColor: dividerColor,
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>

                  {showSliderDots && (
                    <div className="flex space-x-2">
                      {testimonials.map((_, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => goToSlide(index)}
                          className="w-3 h-3 rounded-full transition-colors duration-200"
                          style={{
                            backgroundColor:
                              index === activeSlide
                                ? sliderDotsActiveColor
                                : sliderDotsColor,
                          }}
                          aria-label={`Go to slide ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={nextSlide}
                    className="p-2 rounded-full transition-colors duration-200"
                    style={{
                      color: buttonColor,
                      backgroundColor: dividerColor,
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestimonialsSection;
